import React, { useRef, useState, useEffect } from 'react';
import { UploaderComponent } from '@syncfusion/ej2-react-inputs';
import './OpImageUploader.css';
import { formatFileSize, getFileName } from './opImageUploader.helper';

/**
 * Reusable Logo Uploader component built on top of Syncfusion UploaderComponent.
 * Handles its own preview state while passing base64 formatted data to the parent.
 *
 * @param {Object} props - Component props.
 * @param {string} [props.value] - Preloaded base64 image string passed from the parent.
 * @param {string} [props.allowedExtensions='.png, .jpg, .jpeg, .svg'] - Allowed file types.
 * @param {number} [props.maxFileSize=2097152] - Maximum file size in bytes (default 2MB).
 * @param {Function} [props.onChange] - Callback passing the base64 string and file details to parent.
 * @param {string} [props.placeholder='Image'] - The text displayed when there is no image uploaded.
 *
 * @returns {JSX.Element} Rendered image uploader component.
 */
export function OpImageUploader({
  allowedExtensions = '.png, .jpg, .jpeg, .svg',
  maxFileSize = 2097152, // 2MB
  onChange,
  placeholder = 'Image',
  value,
  ...uploaderParams
}) {
  const dropAreaRef = useRef(null);
  const uploaderRef = useRef(null);
  const dropAreaId = 'op-image-drop-area';

  const [fileDetails, setFileDetails] = useState({
    preview: null,
    name: '',
    sizeFormatted: '',
  });

  // Automatically generate preview when parent passes 'value' (File or String URL)
  useEffect(() => {
    let previewUrl = null;

    if (value instanceof File) {
      // Create a temporary browser URL to display the raw File object
      previewUrl = URL.createObjectURL(value);
    } else if (typeof value === 'string') {
      // If the backend returns a standard image URL string
      previewUrl = value;
    }

    setFileDetails({
      preview: previewUrl,
      name: getFileName(value),
      sizeFormatted: value instanceof File ? formatFileSize(value.size) : '',
    });

    // Cleanup: Prevent memory leaks by revoking the temporary URL when unmounting or changing files
    return () => {
      if (value instanceof File && previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [value]);

  const handleBrowseClick = (e) => {
    e.preventDefault();
    if (dropAreaRef.current) {
      const sfBrowseButton = dropAreaRef.current.querySelector(
        '.e-file-select-wrap button',
      );
      if (sfBrowseButton) sfBrowseButton.click();
    }
  };

  const handleSelected = (args) => {
    args.cancel = true; // Prevent Syncfusion's default upload lifecycle

    if (args.filesData && args.filesData.length > 0) {
      // Get the native browser File object directly from Syncfusion
      const rawFile = args.filesData[0].rawFile;

      if (onChange) {
        // Return ONLY the raw file exactly as requested
        onChange(rawFile);
      }

      if (uploaderRef.current) {
        uploaderRef.current.clearAll();
      }
    }
  };

  const handleRemove = (e) => {
    e.preventDefault();
    if (onChange) {
      onChange(null);
    }
  };

  const hasImage = Boolean(fileDetails.preview);
  const smallCasePlaceholder = placeholder.toLowerCase();

  return (
    <div
      id={dropAreaId}
      className="op-image-uploader-wrapper"
      ref={dropAreaRef}
    >
      {/* SECTION 1: Image Preview Container */}
      <div className={`op-image-placeholder ${hasImage ? 'has-image' : ''}`}>
        {hasImage ? (
          <img src={fileDetails.preview} alt={`Uploaded ${smallCasePlaceholder} preview`} />
        ) : (
          <span>{placeholder}</span>
        )}
      </div>

      {/* SECTION 2: Content Area (Details & Actions) */}
      <div className="op-image-content">
        <div className="op-image-top-row">
          {hasImage ? (
            <>
              <div className="op-image-details">
                <span className="op-image-filename">{fileDetails.name}</span>
                {fileDetails.sizeFormatted && (
                  <span className="op-image-filesize">
                    {fileDetails.sizeFormatted}
                  </span>
                )}
              </div>
              <button
                type="button"
                className="op-image-trash-btn"
                onClick={handleRemove}
                title="Remove image"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="3 6 5 6 21 6"></polyline>
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                  <line x1="10" y1="11" x2="10" y2="17"></line>
                  <line x1="14" y1="11" x2="14" y2="17"></line>
                </svg>
              </button>
            </>
          ) : (
            <div className="op-image-empty-text">
              <h4 className="op-image-title">No {smallCasePlaceholder} uploaded</h4>
              <p className="op-image-subtitle">
                Accepts PNG or JPG. Max 2 MB. Recommended size 200 × 200 px.
              </p>
            </div>
          )}
        </div>

        <div className="op-image-bottom-row">
          <span className="op-image-drop-text">Drop image files here or</span>
          <button
            type="button"
            className="op-image-browse-btn"
            onClick={handleBrowseClick}
          >
            Browse
          </button>
        </div>
      </div>

      {/* SECTION 3: Hidden Syncfusion Core */}
      <div className="op-image-uploader-hidden">
        <UploaderComponent
          ref={uploaderRef}
          {...uploaderParams}
          dropArea={`#${dropAreaId}`}
          allowedExtensions={allowedExtensions}
          maxFileSize={maxFileSize}
          multiple={false}
          showFileList={false}
          selected={handleSelected}
        />
      </div>
    </div>
  );
}